/**
 * Die Dokumentmarke, mit der eine Antwort ein Dokument des eigenen Korpus
 * zitiert -- kurz `(dokumentId:<uuid>)` oder angereichert
 * `(dokumentId:<uuid>|<titel>|<quelleUrl>)`.
 *
 * Eine einzige Form für alle Stellen, die eine Marke erkennen, schützen,
 * bauen oder darstellen. Der Titel trägt jedes Zeichen außer dem
 * Trennzeichen "|" und dem Zeilenumbruch; Klammern gehören im AGB-Korpus zum
 * Namen ("Allgemeine Österreichische Spediteurbedingungen (AÖSp)"). Die Marke
 * endet deshalb nicht an der ersten Klammer, sondern an der Klammer hinter
 * dem dritten Feld.
 *
 * Gelesen wird großzügiger als geschrieben: als drittes Feld gilt jeder Text
 * bis zur schließenden Klammer. Marken aus älteren Ständen und von der
 * Gegenstelle, deren drittes Feld keine Adresse ist, bleiben damit eine Marke
 * und zerfallen nicht in Bruchstücke; ob daraus ein Verweis wird, entscheidet
 * beim Darstellen allein `safeHttpUrl`. Geschrieben wird ausschließlich eine
 * http(s)-Adresse ohne Leerraum, ohne "|" und ohne ")" (markerSafeUrl) --
 * prozentkodierte Klammern (%28/%29) bleiben damit erhalten -- oder ein leeres
 * drittes Feld.
 *
 * Jedes Feld hat genau eine Zeichenklasse ohne überlappenden Nachbarn: der
 * Ausdruck bleibt auch auf einer Marke ohne schließende Klammer linear.
 *
 * Das Gegenstück im Backend ist `backend/src/shared/documentMarker.ts`; beide
 * Seiten beschreiben dieselbe Marke und sind gemeinsam zu ändern.
 */

/** Kennung eines Dokuments, wie sie die Marke trägt. */
export const DOCUMENT_UUID_SOURCE =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/** Titel der Marke: alles außer Trennzeichen und Zeilenumbruch. */
const MARKER_TITLE_SOURCE = '[^|\\n]*';

/**
 * Drittes Feld beim Lesen: alles bis zur schließenden Klammer. Eine einzige
 * Zeichenklasse -- zwei Klassen nebeneinander (etwa Leerraum, Adresse,
 * Leerraum) teilten eine Leerraumkette mehrdeutig auf und liessen den Ausdruck
 * auf einer Marke ohne schließende Klammer quadratisch zurücklaufen.
 */
const MARKER_TRAILER_SOURCE = '[^|\\n)]*';

/** Die Marke ohne Fanggruppen -- zum Erkennen, Schützen und Zerlegen. */
export const DOCUMENT_MARKER_SOURCE =
  `\\(dokumentId:[ \\t]*${DOCUMENT_UUID_SOURCE}`
  + `(?:\\|${MARKER_TITLE_SOURCE}\\|${MARKER_TRAILER_SOURCE})?\\)`;

/** Dieselbe Marke mit Fanggruppen: Kennung, Titel, drittes Feld. */
export const DOCUMENT_MARKER_PARTS_SOURCE =
  `\\(dokumentId:[ \\t]*(${DOCUMENT_UUID_SOURCE})`
  + `(?:\\|(${MARKER_TITLE_SOURCE})\\|(${MARKER_TRAILER_SOURCE}))?\\)`;

/** Frischer Ausdruck je Aufruf, damit kein `lastIndex` geteilt wird. */
export const documentMarkerPattern = (flags: string = 'gi'): RegExp =>
  new RegExp(DOCUMENT_MARKER_SOURCE, flags);

export const documentMarkerPartsPattern = (flags: string = 'gi'): RegExp =>
  new RegExp(DOCUMENT_MARKER_PARTS_SOURCE, flags);

/**
 * Titel, wie ihn die Marke tragen darf: Trennzeichen und Steuerzeichen
 * weichen einem Leerzeichen, Leerraum wird zusammengezogen. Klammern bleiben
 * stehen -- sie gehören zum Namen des Dokuments.
 */
export function markerSafeTitle(value: string): string {
  return value.replace(/[|\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Adresse, wie sie die Marke tragen darf, sonst null: http(s), ohne Leerraum,
 * ohne Trennzeichen und ohne schließende Klammer.
 */
export function markerSafeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^https?:\/\/[^\s|)]*$/i.test(trimmed) ? trimmed : null;
}

/**
 * Baut die Marke. Ohne Titel und ohne Adresse bleibt die kurze Form; sonst
 * die angereicherte, deren drittes Feld leer bleiben darf.
 */
export function buildDocumentMarker(
  documentId: string,
  title: string,
  url: unknown,
): string {
  const text = markerSafeTitle(title);
  const source = markerSafeUrl(url);
  if (text.length === 0 && source === null) return `(dokumentId:${documentId})`;
  return `(dokumentId:${documentId}|${text}|${source ?? ''})`;
}
